/**
 * ------------------------------------------------------------
 * CUSTOM PERSONALIZATION ENGINE - HIGH TRAFFIC PRODUCTION (V35)
 * ------------------------------------------------------------
 * Arquitectura orientada a eventos pura. Cero MutationObservers.
 * Rendimiento optimizado para soportar miles de visitas concurrentes sin bucles de red.
 * Máxima compatibilidad con Symmetry en dispositivos móviles y de escritorio.
 * ------------------------------------------------------------
 */

const CONFIG = {
  feeVariantId: window.PersonalizationConfig?.feeVariantId ? parseInt(window.PersonalizationConfig.feeVariantId, 10) : null,
  feeDelayMs: 350, // Tiempo de estabilización (debounce) para agrupar llamadas consecutivas
  bannedWords: Array.isArray(window.PersonalizationConfig?.bannedWords) 
    ? window.PersonalizationConfig.bannedWords.map(w => String(w).toUpperCase().trim()) 
    : [],
  maxNameLength: window.PersonalizationConfig?.maxNameLength ? parseInt(window.PersonalizationConfig.maxNameLength, 10) : 15
};

class ProductPersonalization {
  constructor(container) {
    this.container = container;
    this.form = container.closest('form[action^="/cart/add"]');
    
    if (!this.form) return;

    // VALIDACIÓN PREVENTIVA DE ENTORNO
    if (!CONFIG.feeVariantId || CONFIG.feeVariantId < 1000000000000) {
      console.error('🚫 [Personalización Error]: ID de metacomp "custom.fee_variant_id" inválido o vacío.');
      this.container.hidden = true;
      this.container.style.display = 'none';
      return;
    }

    this.typeInputs = container.querySelectorAll('input[name="personalization_type"]');
    this.playerSection = container.querySelector('[data-player-section]');
    this.customSection = container.querySelector('[data-custom-section]');
    this.playerSelect = container.querySelector('[data-player-select]');
    this.customNumber = container.querySelector('[data-custom-number]');
    this.customName = container.querySelector('[data-custom-name]');
    
    this.isProcessing = false;
    this.init();
  }

  init() {
    const checked = this.container.querySelector('input[name="personalization_type"]:checked');
    if (checked) this.toggleSections(checked.value);

    this.typeInputs.forEach((input) => {
      input.addEventListener('change', () => {
        this.toggleSections(input.value);
        this.clearError();
      });
    });

    if (this.customName) {
      this.customName.setAttribute('maxlength', CONFIG.maxNameLength);
      
      this.customName.addEventListener('input', (e) => {
        this.clearError();
        e.target.value = this.sanitizeText(e.target.value);
      });

      this.customName.addEventListener('paste', (e) => {
        e.preventDefault();
        const pastedText = (e.clipboardData || window.clipboardData).getData('text');
        const sanitized = this.sanitizeText(pastedText);
        
        const start = e.target.selectionStart;
        const end = e.target.selectionEnd;
        const currentVal = e.target.value;
        
        e.target.value = currentVal.substring(0, start) + sanitized + currentVal.substring(end);
        e.target.setSelectionRange(start + sanitized.length, start + sanitized.length);
      });
    }

    if (this.customNumber) {
      this.customNumber.addEventListener('input', (e) => {
        e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
      });
    }

    this.form.addEventListener('submit', (e) => {
      this.handleSubmit(e);
    }, { capture: true });
  }

  sanitizeText(text) {
    let clean = text.toUpperCase().replace(/[^A-ZÑÇÁÉÍÓÚÀÈÌÒÙÄËÏÖÜ .\-]/g, '');
    
    const containsBanned = CONFIG.bannedWords.some(word => word !== '' && clean.includes(word));
    if (containsBanned) {
      this.showError('El nombre contiene vocabulario o palabras no permitidas.');
      CONFIG.bannedWords.forEach(word => {
        if (word !== '') clean = clean.replaceAll(word, '');
      });
    }
    return clean.slice(0, CONFIG.maxNameLength);
  }

  toggleSections(type) {
    if(this.playerSection) this.playerSection.hidden = type !== 'player';
    if(this.customSection) this.customSection.hidden = type !== 'custom';
    this.clearError();
  }

  showError(message) {
    let errorContainer = this.container.querySelector('.personalization-error-msg');
    if (!errorContainer) {
      errorContainer = document.createElement('div');
      errorContainer.className = 'personalization-error-msg';
      this.container.appendChild(errorContainer);
    }
    errorContainer.textContent = message;
  }

  clearError() {
    const errorContainer = this.container.querySelector('.personalization-error-msg');
    if (errorContainer) errorContainer.remove();
  }

  async handleSubmit(e) {
    const activeInput = this.container.querySelector('input[name="personalization_type"]:checked');
    const activeType = activeInput ? activeInput.value : 'none';
    
    if (activeType === 'none') return; 

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    if (this.isProcessing) return;
    this.isProcessing = true;
    this.clearError();

    let personalizationData = null;

    if (activeType === 'player') {
      const selectedOption = this.playerSelect.options[this.playerSelect.selectedIndex];
      if (!selectedOption || !selectedOption.value) {
        this.showError('Por favor, selecciona un jugador del equipo.');
        this.isProcessing = false;
        return;
      }
      personalizationData = { name: selectedOption.value.toUpperCase(), number: selectedOption.dataset.number };
    } else if (activeType === 'custom') {
      const nameVal = this.customName.value.trim().toUpperCase();
      const numVal = this.customNumber.value.trim();

      if (!nameVal && !numVal) {
        this.showError('Por favor, introduce el nombre y el número que deseas estampar.');
        this.isProcessing = false;
        return;
      }

      const containsBanned = CONFIG.bannedWords.some(word => word !== '' && nameVal.includes(word));
      if (containsBanned) {
        this.showError('No se puede añadir al carrito: Vocabulario no permitido.');
        this.isProcessing = false;
        return;
      }

      // 2. Normalizamos la salida para el carrito: si no hay valor, enviamos un guion descriptivo
      personalizationData = { 
        name: nameVal || '-', 
        number: numVal || '-' 
      };
    }

    if (personalizationData) {
      const submitBtn = this.form.querySelector('[type="submit"], [name="add"], .ajax-submit');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('is-loading');
      }

      const mainVariantId = parseInt(this.form.querySelector('[name="id"]')?.value || new FormData(this.form).get('id'), 10);
      const quantity = parseInt(this.form.querySelector('[name="quantity"]')?.value || '1', 10);
      
      if (!mainVariantId) {
        this.showError('Error interno del producto al procesar la compra.');
        this.isProcessing = false;
        if (submitBtn) submitBtn.disabled = false;
        return;
      }

      const bundleGroupId = `bundle_${Date.now()}`;
      const productTitle = document.querySelector('.product-title, h1')?.textContent?.trim() || 'Camiseta Oficial';

      const payload = {
        items: [
          {
            id: CONFIG.feeVariantId,
            quantity: quantity,
            properties: {
              'PERSONALIZACIÓN': productTitle,
              'Nombre': `${personalizationData.name}  Dorsal: ${personalizationData.number}`,
              '_bundle_group': bundleGroupId
            }
          },
          {
            id: mainVariantId,
            quantity: quantity,
            properties: {
              'Personalización': `${personalizationData.name} (${personalizationData.number})`,
              '_bundle_group': bundleGroupId
            }
          }
        ]
      };

      try {
        const response = await fetch((window.Shopify.routes.root || '/') + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || 'No hay stock suficiente.');
        }

        // Emitimos evento de éxito nativo antes de actualizar la UI del tema
        document.dispatchEvent(new CustomEvent('personalization:added'));

        if (window.location.pathname.includes('/cart')) {
          this.refreshAllCartComponents();
          return;
        }

        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { 
          detail: { opener: submitBtn } 
        }));

      } catch (error) {
        console.error('Error en pasarela de personalización:', error);
        this.showError(error.message || 'Hubo un problema al guardar tu personalización.');
        this.refreshAllCartComponents();
      } finally {
        this.isProcessing = false;
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.classList.remove('is-loading');
        }
      }
    }
  }

  refreshAllCartComponents() {
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
    const cartFormElement = document.querySelector('cart-form');
    if (cartFormElement && typeof cartFormElement.refresh === 'function') {
      cartFormElement.refresh();
    }
  }
}

/**
 * ------------------------------------------------------------
 * SYMMETRY BUNDLE LINKER & ENFORCER - PURE EVENT EDITION
 * ------------------------------------------------------------
 * Escucha atómica de eventos e interacciones. Cero consultas redundantes.
 * ------------------------------------------------------------
 */
class CartBundleEnforcer {
  constructor() {
    if (!CONFIG.feeVariantId || CONFIG.feeVariantId < 1000000000000) return;

    this.isFixing = false; 
    this.integrityAbortController = null; 
    this.init();
  }

  init() {
    // 1. Escuchamos cambios físicos de cantidad en inputs nativos dentro del carrito
    document.addEventListener('change', (e) => {
      if (e.target.closest('cart-form, .cart-drawer, [data-gift-wrap], .cart-item__qty')) {
        this.verifyCartIntegrity();
      }
    });

    // 2. Interceptamos clicks en los botones de sumar/restar o eliminar del tema
    document.addEventListener('click', (e) => {
      if (e.target.closest('.qty-btn, .cart-toggle, .remove-btn, .cart-item__remove, [href*="/cart/change"]')) {
        this.verifyCartIntegrity();
      }
    });

    // 3. Conexión directa con el ciclo de vida de eventos del propio tema Symmetry
    document.addEventListener('on:cart:after-merge', () => this.verifyCartIntegrity());
    document.addEventListener('dispatch:cart-drawer:refresh', () => this.verifyCartIntegrity());
    document.addEventListener('personalization:added', () => this.verifyCartIntegrity());

    // Verificación única inicial pasiva
    this.verifyCartIntegrity();
  }

  verifyCartIntegrity() {
    if (this.isFixing) return;

    clearTimeout(this.integrityTimer);
    // Agrupamos posibles eventos simultáneos (Debounce) para hacer solo una petición
    this.integrityTimer = setTimeout(() => {
      if (this.integrityAbortController) this.integrityAbortController.abort();
      this.integrityAbortController = new AbortController();
      
      fetch((window.Shopify.routes.root || '/') + 'cart.js', { signal: this.integrityAbortController.signal })
        .then(res => res.json())
        .then(cart => {
          const updates = {};
          let needsFix = false;
          const bundles = {};

          cart.items.forEach(item => {
            const bg = item.properties && item.properties['_bundle_group'];
            
            if (bg) {
              if (!bundles[bg]) bundles[bg] = { parentQty: 0, fees: [] };
              
              if (item.variant_id === CONFIG.feeVariantId) {
                bundles[bg].fees.push({ key: item.key, qty: item.quantity });
              } else {
                bundles[bg].parentQty += item.quantity;
              }
            } else if (item.variant_id === CONFIG.feeVariantId) {
              // Tasa suelta sin grupo vinculador: marcada para eliminación inmediata
              updates[item.key] = 0;
              needsFix = true;
            }
          });

          Object.keys(bundles).forEach(bg => {
            const bundle = bundles[bg];
            const feeTotalQty = bundle.fees.reduce((sum, f) => sum + f.qty, 0);

            if (bundle.parentQty === 0 && feeTotalQty > 0) {
              bundle.fees.forEach(f => { updates[f.key] = 0; });
              needsFix = true;
            } 
            else if (bundle.parentQty > 0 && feeTotalQty !== bundle.parentQty) {
              if (bundle.fees.length > 0) {
                updates[bundle.fees[0].key] = bundle.parentQty;
                for (let i = 1; i < bundle.fees.length; i++) {
                  updates[bundle.fees[i].key] = 0;
                }
                needsFix = true;
              }
            }
          });

          // BLINDAJE DE CONEXIÓN: Solo modificamos el carrito si detectamos un error real de integridad
          if (needsFix) {
            this.executeCartFix(updates);
          } else {
            // Si las cantidades están perfectas, inyectamos los estilos CSS pasivos sin hacer peticiones a red
            this.applyVisualHierarchy();
          }
        })
        .catch(err => { if (err.name !== 'AbortError') console.error(err); });
        
    }, CONFIG.feeDelayMs); 
  }

  executeCartFix(updates) {
    this.isFixing = true;
    const sectionsToFetch = "cart-drawer,main-cart-items,main-cart-footer,cart-icon-bubble";

    fetch((window.Shopify.routes.root || '/') + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ updates: updates, sections: sectionsToFetch })
    })
    .then(res => res.json())
    .then(() => {
      this.applyVisualHierarchy();
      
      // Sincronizamos los formularios nativos del tema
      document.querySelectorAll('cart-form').forEach(form => {
        if (form && typeof form.refresh === 'function') form.refresh();
      });

      // Periodo de enfriamiento de red extendido (Anti-spam)
      setTimeout(() => { this.isFixing = false; }, 500);
    })
    .catch(() => { this.isFixing = false; });
  }

  applyVisualHierarchy() {
    const cartItems = document.querySelectorAll('.cart-item, [data-bundle-item], .cart-drawer__item, .drawer-cart-item');
    if (!cartItems.length) return;

    cartItems.forEach(item => {
      if (item.classList.contains('custom-child-fee')) return;
      const text = item.textContent;
      if (text.includes(CONFIG.feeVariantId.toString()) || text.includes('PERSONALIZACIÓN')) {
        item.classList.add('custom-child-fee');
      }
    });
  }
}

// Inicialización asíncrona segura
document.addEventListener('DOMContentLoaded', () => {
  new CartBundleEnforcer();
  document.querySelectorAll('[data-personalization-block]').forEach(block => new ProductPersonalization(block));
});